<template>
  <div id="page-private-plot-list">
    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center mb-base">
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2"
                >{{ currentPageStart }}
                -
                {{ currentPageEnd }}
                / {{ totalPlots }}</span
              >
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="onChangePageSize(10)">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="onChangePageSize(20)">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="onChangePageSize(30)">
                <span>30</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
      </div>
      <ag-grid-vue
        ref="agGridTable"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :components="components"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="privatePlotsForPagination"
        rowSelection="multiple"
        colResizeDefault="shift"
        :animateRows="true"
        :floatingFilter="false"
        :pagination="true"
        :paginationPageSize="paginationPageSize"
        :suppressPaginationPanel="true"
        :suppressDragLeaveHidesColumns="true"
        :suppressNoRowsOverlay="true"
        @grid-ready="onGridReady"
        @rowClicked="onRowClicked"
      >
      </ag-grid-vue>
      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import CellRendererLink from "./cell-renderer/CellRendererLink.vue";
import CellRendererStatus from "./cell-renderer/CellRendererStatus.vue";
import CellRendererVerified from "./cell-renderer/CellRendererVerified.vue";
import CellRendererActions from "./cell-renderer/CellRendererActions.vue";
import CellRendererPlotDate from "./cell-renderer/CellRendererPlotDate";
import CellRendererPlotCategory from "./cell-renderer/CellRendererPlotCategory";
import { createNamespacedHelpers } from "vuex";
import AuthService from "@/services/authService";
import FireStoreService from "@/services/fireStoreService";

const {
  mapActions: mapActionsOfPrivatePlot,
  mapGetters: mapGettersOfPrivatePlot,
} = createNamespacedHelpers("gkmgPrivatePlot");
const INITIAL_PAGE_SIZE = 10;

export default {
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererLink,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererStatus,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererVerified,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererPlotDate,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererPlotCategory,
  },
  data() {
    return {
      gridApi: null,
      gridOptions: {
        localeText: { noRowsToShow: "プロットが見つかりませんでした" },
        context: {
          componentParent: this,
        },
        rowData: null,
        columnDefs: null,
      },
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true,
      },
      components: {
        CellRendererLink,
        CellRendererStatus,
        CellRendererVerified,
        CellRendererActions,
      },
      currentPage: 1,
      paginationPageSize: INITIAL_PAGE_SIZE, // 一覧に表示するレコードの数
    };
  },
  watch: {
    currentPage() {
      let page = this.currentPage - 1;
      this.gridApi.paginationGoToPage(page); // 一覧のコンポーネントに表示するページを反映
    },
    pageableInfo() {
      this.createPrivatePlotPageable(this.pageableInfo);
    },
  },
  computed: {
    ...mapGettersOfPrivatePlot(["getPageable", "getEnterprisePrivatePlots"]),
    pageableInfo() {
      return {
        enterprisePrivatePlots: this.enterprisePrivatePlots,
        pageSize: this.paginationPageSize,
      };
    },
    enterprisePrivatePlots() {
      return this.getEnterprisePrivatePlots();
    },
    privatePlotsForPagination() {
      // 取得している法人のプライベートプロットを全件返す
      // 他のページと異なり、ページサイズ変更時には一覧のコンポーネントには常に全件のデータを渡し、ページネーションの設定を変更する
      // モジュールバージョンアップの影響か、一覧のコンポーネントに渡すデータの値の変化が画面上の表示に反映されないため
      // (現状確認した限りでは、反映するにはデータをサーバ側から取り直すなど、stateを更新する処理をはさむ必要があった)
      return this.enterprisePrivatePlots;
    },
    // ページネーションの値
    totalPages() {
      if (this.getPageable.total_pages) return this.getPageable.total_pages;
      else return 0;
    },
    // プライベートプロットの数
    totalPlots() {
      if (this.getPageable) return this.getPageable.total_elements;
      else return 0;
    },
    currentPageStart() {
      const pageStart =
        this.currentPage * this.paginationPageSize -
        (this.paginationPageSize - 1);
      return pageStart;
    },
    currentPageEnd() {
      const pageEnd =
        this.totalPlots - this.currentPage * this.paginationPageSize > 0
          ? this.currentPage * this.paginationPageSize
          : this.totalPlots;
      return pageEnd;
    },
  },
  methods: {
    ...mapActionsOfPrivatePlot([
      "createPrivatePlotPageable",
      "createPrivatePlots",
    ]),
    onGridReady() {
      this.autoSizeAll();
    },

    onRowClicked(evt) {
      if (
        evt.event.target.classList.contains("vs-button") ||
        evt.event.target.classList.contains("evt-actions") ||
        evt.event.target.classList.contains("vs-button-text")
      ) {
        return;
      } else {
        this.$router.push({
          name: "enterprise-private-plot-edit",
          params: { hash: evt.data.hash },
        });
      }
    },
    autoSizeAll() {
      let allColumnIds = [];
      this.gridColumnApi.getAllColumns().forEach(function (column) {
        allColumnIds.push(column.getId());
      });
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    },

    onChangePageSize(size) {
      this.paginationPageSize = size;
      this.currentPage = 1;
      this.gridApi.paginationSetPageSize(this.paginationPageSize); // ページサイズを変更
    },
    setColumnDefs() {
      this.columnDefs = [
        {
          headerName: "プロット名",
          field: "title",
        },
        {
          headerName: "種別",
          field: "plot_child_category_id",
          cellRenderer: "CellRendererPlotCategory",
        },
        {
          headerName: "コメント",
          field: "comment",
        },
        {
          headerName: "表示開始日時(JST)",
          field: "start_date",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "表示終了日時(JST)",
          field: "end_date",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "作成日時(JST)",
          field: "created_at",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "更新日時(JST)",
          field: "updated_at",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "アクション",
          field: "hash",
          cellRenderer: "CellRendererActions",
          cellClass: "actions",
        },
      ];
    },
  },
  async mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";

    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  async created() {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    // 法人IDに紐づくプライベートプロット
    const enterprisePrivatePlots = await FireStoreService.getPrivatePlots(
      enterpriseId,
    );
    this.createPrivatePlots(enterprisePrivatePlots);
  },
  beforeMount() {
    this.setColumnDefs();
  },
};
</script>

<style lang="scss">
#page-private-plot-list {
  .vx-input-group .prepend-text.btn-addon .vs-button {
    padding-top: -10px !important;
  }

  .vs-input-primary {
    width: 400px;
  }

  .plot-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }

  .ag-grid-cell-chip {
    border-radius: 5px;

    &.vs-chip-success {
      background: rgba(var(--vs-success), 0.15);
      color: rgba(var(--vs-success), 1) !important;
      font-weight: 500;
    }

    &.vs-chip-warning {
      background: rgba(var(--vs-warning), 0.15);
      color: rgba(var(--vs-warning), 1) !important;
      font-weight: 500;
    }

    &.vs-chip-danger {
      background: rgba(var(--vs-danger), 0.15);
      color: rgba(var(--vs-danger), 1) !important;
      font-weight: 500;
    }
  }
}
</style>
